import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import Header from "../components/Header";
import { toast } from "sonner";
import { Wallet, Users, Plus, Send, Loader2, CheckCircle2, Clock, Phone as PhoneIcon, AlertCircle, XCircle, Copy, Share2, Link as LinkIcon, Sprout, Target, Flame, Gem, Lock, Trophy, FileDown, Award, Medal, Crown, Sparkles } from "lucide-react";

const TIER_ICONS = { Sparkles, Award, Medal, Crown, Gem };

const STATUS_META = {
  pending: { label: "In attesa conferma", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  confirmed: { label: "Confermato dall'amico", color: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle2 },
  contacted: { label: "Contattato da Besafe", color: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: PhoneIcon },
  subscribed: { label: "Polizza sottoscritta", color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2 },
  expired: { label: "Scaduto", color: "bg-slate-100 text-slate-600 border-slate-200", icon: AlertCircle },
  rejected: { label: "Rifiutato", color: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
};

const BADGE_LABELS = {
  pioniere: "Pioniere",
  networker: "Networker",
  cecchino: "Cecchino",
  in_serie: "In Serie",
  hall_of_fame: "Hall of Fame",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const reload = async () => {
    try {
      const [w, r, b] = await Promise.all([api.get("/wallet"), api.get("/referrals"), api.get("/badges")]);
      setWallet(w.data);
      setReferrals(r.data);
      setBadges(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // Fire-and-forget visit ping used by the 24h completion-reminder automation.
    api.post("/me/dashboard-opened").catch(() => { /* silently ignore */ });
  }, []);

  const balance = wallet?.balance ?? 0;
  const max = wallet?.max_balance ?? 100;
  const pct = Math.min(100, Math.round((balance / max) * 100));

  return (
    <div className="min-h-screen bg-[#F7F8F9]" data-testid="dashboard-page">
      <Header variant="app" />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Area personale</div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[#0F7B85] tracking-tight">
              Ciao {user?.name?.split(" ")[0] || "cliente"} 👋
            </h1>
            <p className="text-slate-600 mt-1">Gestisci segnalazioni e abbuoni in un unico posto.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/regolamento.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dashboard-regolamento-link"
              className="text-sm text-slate-600 hover:text-[#0F7B85] inline-flex items-center gap-1.5 border border-slate-200 bg-white hover:border-[#0F7B85] rounded-lg px-3 py-2.5 transition-colors"
            >
              <FileDown className="w-4 h-4"/> Regolamento
            </a>
            <button
              onClick={() => setShowForm((v) => !v)}
              data-testid="toggle-referral-form-button"
              className="bg-[#F39C12] text-white hover:bg-[#E08E10] rounded-lg px-6 py-3 font-medium transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4"/> Presenta un amico
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Wallet Card */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-8" data-testid="wallet-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0F7B85] flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Saldo abbuono</div>
                <div className="font-display text-3xl text-[#0F7B85]" data-testid="wallet-balance">
                  {balance}€ <span className="text-slate-400 text-lg font-light">/ {max}€</span>
                </div>
              </div>
            </div>
            <div className="mt-6 w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-[#0F7B85] to-[#0A5C64] h-full transition-all duration-700 ease-out"
                style={{ width: `${pct}%` }}
                data-testid="wallet-progress"
              />
            </div>
            <div className="mt-3 flex justify-between text-xs text-slate-500">
              <span>0€</span>
              <span className="font-mono">{pct}%</span>
              <span>{max}€</span>
            </div>
            <p className="mt-4 text-sm text-slate-600">
              {balance < max
                ? `Mancano ${max - balance}€ per raggiungere il tetto massimo. Sono ${Math.ceil((max - balance) / 10)} amici!`
                : "Hai raggiunto il tetto massimo di 100€. Verrà scontato dalla prossima scadenza polizza."}
            </p>
          </div>

          {/* KPI */}
          <div className="bg-[#0F7B85] text-white rounded-2xl p-8 flex flex-col justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5"/>
              <div className="text-xs uppercase tracking-[0.2em] opacity-70">Le tue segnalazioni</div>
            </div>
            <div className="mt-6">
              <div className="font-display text-5xl" data-testid="referrals-total">{referrals.length}</div>
              <div className="text-sm opacity-80 mt-1">
                {referrals.filter(r => r.status === "subscribed").length} sottoscritte ·{" "}
                {referrals.filter(r => r.status === "pending").length} in attesa
              </div>
            </div>
          </div>
        </div>

        {showForm && (
          <ReferralForm
            onCreated={async () => { setShowForm(false); await reload(); }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {/* Share via WhatsApp */}
        {user?.referral_code && <ShareCard user={user} />}

        {/* Tier (Bronze/Silver/Gold/Diamond) */}
        {wallet?.tier && <TierCard tier={wallet.tier} />}

        {/* Badges */}
        {badges.length > 0 && <BadgeShelf badges={badges} />}

        {/* Referrals list */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-[#0F7B85]">Storico segnalazioni</h2>
          </div>
          {loading ? (
            <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Caricamento…</div>
          ) : referrals.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center" data-testid="empty-referrals">
              <div className="text-slate-500">Non hai ancora segnalato nessun amico.</div>
              <button
                onClick={() => setShowForm(true)}
                data-testid="empty-cta-add-referral"
                className="mt-4 bg-[#0F7B85] text-white hover:bg-[#0A5C64] rounded-lg px-5 py-2.5 text-sm font-medium transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4"/> Inizia adesso
              </button>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
              <table className="w-full" data-testid="referrals-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="py-3 px-4 text-left">Targa</th>
                    <th className="py-3 px-4 text-left">Amico</th>
                    <th className="py-3 px-4 text-left">Stato</th>
                    <th className="py-3 px-4 text-left">Data</th>
                    <th className="py-3 px-4 text-right">Abbuono</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.pending;
                    const Icon = meta.icon;
                    return (
                      <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-testid={`referral-row-${r.id}`}>
                        <td className="py-4 px-4 font-mono text-sm text-[#0F7B85] uppercase">{r.plate}</td>
                        <td className="py-4 px-4 text-sm">
                          <div className="font-medium text-slate-800">{r.friend_name}</div>
                          <div className="text-slate-500 text-xs">{r.friend_email}</div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex flex-col items-start gap-1.5">
                            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${meta.color}`}>
                              <Icon className="w-3 h-3"/> {meta.label}
                            </span>
                            {r.status === "pending" && typeof r.days_to_expiry === "number" && (
                              <ExpiryCountdown referral={r} onReminderSent={reload} />
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-600">
                          {new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                        <td className="py-4 px-4 text-sm text-right">
                          {r.credit_awarded ? (
                            <span className="text-[#0F7B85] font-semibold">+10€</span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function TierCard({ tier }) {
  const Icon = TIER_ICONS[tier.current.icon] || Sparkles;
  const NextIcon = tier.next ? (TIER_ICONS[tier.next.icon] || Sparkles) : null;
  const subscribed = tier.subscribed_count;
  const start = tier.current.min_subscribed;
  const end = tier.next ? tier.next.min_subscribed : Math.max(subscribed, start + 1);
  const span = Math.max(1, end - start);
  const filled = Math.max(0, Math.min(span, subscribed - start));
  const pct = Math.round((filled / span) * 100);
  const tiers = [
    { id: "bronze", name: "Bronze", min: 1, color: "#CD7F32" },
    { id: "silver", name: "Silver", min: 4, color: "#94A3B8" },
    { id: "gold", name: "Gold", min: 7, color: "#F39C12" },
    { id: "diamond", name: "Diamond", min: 11, color: "#0EA5E9" },
  ];
  const currentIdx = tiers.findIndex(t => t.id === tier.current.id);
  return (
    <section className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 relative overflow-hidden" data-testid="tier-card">
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ background: `radial-gradient(circle at 90% 10%, ${tier.current.color}, transparent 60%)` }}
      />
      <div className="relative flex items-start justify-between flex-wrap gap-6">
        <div className="flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: `${tier.current.color}20`, color: tier.current.color }}
            data-testid="tier-current-icon"
          >
            <Icon className="w-8 h-8" strokeWidth={2.2}/>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Il tuo livello</div>
            <h2 className="font-display text-3xl mt-1" style={{ color: tier.current.color }} data-testid="tier-current-name">
              {tier.current.name}
            </h2>
            <p className="text-sm text-slate-600 mt-1 max-w-md">{tier.current.tagline}</p>
          </div>
        </div>
        {tier.next ? (
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-2 justify-end">
              Prossimo livello
              {NextIcon && <NextIcon className="w-4 h-4" style={{ color: tier.next.color }} />}
            </div>
            <div className="font-display text-xl mt-1" style={{ color: tier.next.color }} data-testid="tier-next-name">
              {tier.next.name}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Ti {tier.to_next === 1 ? "manca" : "mancano"} <strong className="text-slate-700">{tier.to_next}</strong>
              {" "}{tier.to_next === 1 ? "amico sottoscritto" : "amici sottoscritti"}
            </div>
          </div>
        ) : (
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Top tier</div>
            <div className="font-display text-xl mt-1 text-[#0EA5E9]">Hall of Fame</div>
          </div>
        )}
      </div>

      {/* Progress bar between current and next */}
      {tier.next && (
        <div className="relative mt-6">
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${tier.current.color}, ${tier.next.color})` }}
              data-testid="tier-progress-bar"
            />
          </div>
          <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500 mt-1.5">
            <span>{tier.current.name} · {start} sub</span>
            <span>{tier.next.name} · {end} sub</span>
          </div>
        </div>
      )}

      {/* Tier ladder */}
      <div className="relative mt-6 grid grid-cols-4 gap-2">
        {tiers.map((t, i) => {
          const TIcon = TIER_ICONS[{ bronze: "Award", silver: "Medal", gold: "Crown", diamond: "Gem" }[t.id]] || Sparkles;
          const reached = currentIdx >= i + 1;
          const active = tier.current.id === t.id;
          return (
            <div
              key={t.id}
              data-testid={`tier-step-${t.id}`}
              className={`rounded-xl p-3 border text-center transition-all ${reached ? "bg-white" : "bg-slate-50 opacity-70"} ${active ? "border-2 shadow-sm scale-[1.03]" : "border-slate-200"}`}
              style={active ? { borderColor: t.color } : {}}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center mx-auto"
                style={{ backgroundColor: reached ? `${t.color}1A` : "#E2E8F0", color: reached ? t.color : "#94A3B8" }}
              >
                {reached ? <TIcon className="w-5 h-5" strokeWidth={2.2}/> : <Lock className="w-4 h-4"/>}
              </div>
              <div className={`mt-1.5 text-xs font-display ${reached ? "" : "text-slate-500"}`} style={reached ? { color: t.color } : {}}>
                {t.name}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">{t.min}+ sub</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}



function BadgeShelf({ badges }) {
  const ICON_MAP = { Sprout, Users, Target, Flame, Gem };
  const earnedCount = badges.filter(b => b.earned).length;
  return (
    <section className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="badge-shelf">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#F39C12]"/> I miei traguardi
          </div>
          <h2 className="font-display text-2xl text-[#0F7B85] mt-1">
            {earnedCount} / {badges.length} sbloccati
          </h2>
        </div>
        <div className="text-xs text-slate-500">
          Continua a presentare amici per conquistare tutti i badge
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {badges.map(b => {
          const Icon = ICON_MAP[b.icon] || Sprout;
          return (
            <div
              key={b.id}
              data-testid={`badge-${b.id}`}
              className={`relative rounded-xl p-4 border transition-all ${
                b.earned
                  ? "bg-gradient-to-br from-white to-slate-50 border-slate-200 shadow-sm hover:-translate-y-0.5"
                  : "bg-slate-50 border-slate-200 opacity-70"
              }`}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                style={{
                  backgroundColor: b.earned ? `${b.color}15` : "#E2E8F0",
                  color: b.earned ? b.color : "#94A3B8",
                }}
              >
                {b.earned ? <Icon className="w-6 h-6" strokeWidth={2.2}/> : <Lock className="w-5 h-5"/>}
              </div>
              <div className={`font-display text-base ${b.earned ? "text-[#0F7B85]" : "text-slate-500"}`}>
                {b.name}
              </div>
              <div className="text-xs text-slate-500 mt-1 leading-snug min-h-[2.5rem]">
                {b.earned ? b.description : b.hint}
              </div>
              {b.earned && b.earned_at && (
                <div className="text-[10px] text-slate-400 mt-2 font-mono uppercase tracking-wider">
                  {new Date(b.earned_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ShareCard({ user }) {
  const link = `${window.location.origin}/invito/${user.referral_code}`;
  const message = `Ciao! Ti consiglio Besafe Broker per la RCA della tua auto. Compila in 2 minuti e ricevi un preventivo dedicato: ${link}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare. Copia manualmente.");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Besafe Broker", text: message, url: link });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <section className="mt-8 bg-gradient-to-br from-[#0F7B85] to-[#0A5C64] rounded-2xl p-8 text-white relative overflow-hidden" data-testid="share-card">
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#F39C12]/20 blur-3xl" />
      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Condividi su WhatsApp
          </div>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight">
            Il tuo codice: <span className="font-mono text-[#FCD9A0]" data-testid="referral-code-display">{user.referral_code}</span>
          </h2>
          <p className="mt-3 text-white/80 text-sm leading-relaxed">
            Invia questo link al tuo amico via WhatsApp. Compila in 2 minuti, niente email da confermare.
            Funziona anche se ti rispondono direttamente.
          </p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-lg p-3">
            <LinkIcon className="w-4 h-4 text-white/70 shrink-0"/>
            <div className="text-xs sm:text-sm font-mono truncate flex-1" data-testid="referral-link">{link}</div>
            <button
              onClick={copy} data-testid="copy-link-button"
              className="p-2 hover:bg-white/10 rounded-md transition-colors"
              title="Copia link"
            >
              <Copy className="w-4 h-4"/>
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={waUrl} target="_blank" rel="noopener noreferrer"
              data-testid="whatsapp-share-button"
              className="flex-1 bg-[#25D366] hover:bg-[#22c35e] text-white rounded-lg px-5 py-3 font-medium transition-colors inline-flex items-center justify-center gap-2"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
              Condividi su WhatsApp
            </a>
            <button
              onClick={share} data-testid="native-share-button"
              className="bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white rounded-lg px-5 py-3 font-medium transition-colors inline-flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4"/> Altro
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReferralForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    plate: "", friend_name: "", friend_email: "", friend_phone: "", notes: "",
    consent_privacy: false, consent_owner_aware: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const handle = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_privacy || !form.consent_owner_aware) {
      toast.error("Devi accettare entrambi i consensi obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/referrals", form);
      toast.success("Segnalazione inviata! Il tuo amico riceverà una email a breve.");
      if (data.new_badges && data.new_badges.length > 0) {
        setTimeout(() => {
          data.new_badges.forEach((bid, i) => {
            setTimeout(() => toast.success(`🏆 Badge sbloccato: ${BADGE_LABELS[bid] || bid}`), i * 600);
          });
        }, 800);
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio segnalazione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-8 fade-up" data-testid="referral-form-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Nuova segnalazione</div>
          <h3 className="font-display text-2xl text-[#0F7B85] mt-1">Presenta il tuo amico</h3>
        </div>
        <button onClick={onCancel} className="text-slate-500 hover:text-[#F39C12] text-sm" data-testid="cancel-referral-form">Annulla</button>
      </div>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="referral-form">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
            Targa veicolo <span className="text-slate-400 font-normal">(opzionale)</span>
          </label>
          <input
            type="text" value={form.plate} onChange={handle("plate")}
            data-testid="referral-plate-input"
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 uppercase font-mono focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
            placeholder="Se la conosci, es. AB123CD"
            maxLength={10}
          />
          <p className="text-xs text-slate-400 mt-1.5">Se non la conosci, lasciala vuota: te la fornirà direttamente il tuo amico.</p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nome dell'amico</label>
          <input
            type="text" required value={form.friend_name} onChange={handle("friend_name")}
            data-testid="referral-friend-name-input"
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
            placeholder="Luca Bianchi"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email amico</label>
          <input
            type="email" required value={form.friend_email} onChange={handle("friend_email")}
            data-testid="referral-friend-email-input"
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
            placeholder="luca@email.it"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Cellulare amico</label>
          <input
            type="tel" required value={form.friend_phone} onChange={handle("friend_phone")}
            data-testid="referral-friend-phone-input"
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
            placeholder="+39 333 7654321"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Note (opzionale)</label>
          <textarea
            value={form.notes} onChange={handle("notes")}
            data-testid="referral-notes-input"
            rows={2}
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none resize-none"
            placeholder="Es: il mio amico cerca una RCA per la sua nuova auto"
          />
        </div>

        <div className="md:col-span-2 mt-2 space-y-3 bg-[#F7F8F9] border border-slate-200 rounded-xl p-5">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-privacy-label">
            <input
              type="checkbox" checked={form.consent_privacy} onChange={handle("consent_privacy")}
              data-testid="consent-privacy-checkbox"
              className="mt-1 w-4 h-4 rounded border-slate-300 text-[#0F7B85] focus:ring-[#0F7B85]"
            />
            <span className="text-sm text-slate-700 leading-relaxed">
              <strong>Consenso privacy GDPR.</strong> Ho informato l'amico che i suoi dati saranno trattati da
              Besafe Broker per finalità di preventivazione assicurativa. Riceverà un'email di doppia conferma.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-owner-label">
            <input
              type="checkbox" checked={form.consent_owner_aware} onChange={handle("consent_owner_aware")}
              data-testid="consent-owner-checkbox"
              className="mt-1 w-4 h-4 rounded border-slate-300 text-[#0F7B85] focus:ring-[#0F7B85]"
            />
            <span className="text-sm text-slate-700 leading-relaxed">
              <strong>Consenso del proprietario.</strong> Confermo che il proprietario dell'auto è a conoscenza
              di questa segnalazione ed è d'accordo a essere contattato.
            </span>
          </label>
        </div>

        <div className="md:col-span-2 flex justify-end gap-3 mt-2">
          <button type="button" onClick={onCancel} className="px-5 py-3 text-sm text-slate-600 hover:text-[#F39C12]">
            Annulla
          </button>
          <button
            type="submit" disabled={submitting} data-testid="referral-submit-button"
            className="bg-[#0F7B85] text-white hover:bg-[#0A5C64] disabled:opacity-60 rounded-lg px-6 py-3 font-medium transition-colors inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4" />}
            Invia segnalazione
          </button>
        </div>
      </form>
    </div>
  );
}


function ExpiryCountdown({ referral, onReminderSent }) {
  const [sending, setSending] = useState(false);
  const recentlyReminded = useMemo(() => {
    if (!referral.friend_reminder_sent_at) return false;
    const diffMs = Date.now() - new Date(referral.friend_reminder_sent_at).getTime();
    return diffMs < 7 * 24 * 3600 * 1000;
  }, [referral.friend_reminder_sent_at]);

  const days = referral.days_to_expiry;
  if (days === null || days === undefined) return null;

  let tone, label, Icon;
  if (days <= 0) {
    return null;
  } else if (days <= 30) {
    tone = "bg-red-50 text-red-700 border-red-200";
    Icon = AlertCircle;
    label = days === 1 ? "Ultimo giorno!" : `Ultimi ${days} giorni`;
  } else if (days <= 180) {
    tone = "bg-amber-50 text-amber-700 border-amber-200";
    Icon = Clock;
    const months = Math.round(days / 30);
    label = `In scadenza · ${months} mes${months === 1 ? "e" : "i"}`;
  } else {
    tone = "bg-slate-50 text-slate-500 border-slate-200";
    Icon = Clock;
    const months = Math.round(days / 30);
    label = `Scade fra ${months} mesi`;
  }

  const remind = async () => {
    setSending(true);
    try {
      await api.post(`/referrals/${referral.id}/remind`);
      toast.success("Sollecito inviato all'amico");
      onReminderSent && onReminderSent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-1" data-testid={`expiry-countdown-${referral.id}`}>
      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${tone}`}>
        <Icon className="w-2.5 h-2.5"/> {label}
      </span>
      {days <= 30 && !recentlyReminded && (
        <button
          onClick={remind}
          disabled={sending}
          data-testid={`remind-friend-${referral.id}`}
          className="inline-flex items-center gap-1 text-[10px] text-[#F39C12] hover:text-[#0F7B85] font-medium transition-colors"
        >
          {sending ? <Loader2 className="w-2.5 h-2.5 animate-spin"/> : <Send className="w-2.5 h-2.5"/>}
          Manda un sollecito
        </button>
      )}
      {recentlyReminded && (
        <span className="text-[10px] text-slate-400 italic" data-testid={`remind-friend-cooldown-${referral.id}`}>
          Sollecitato di recente
        </span>
      )}
    </div>
  );
}
